import {
  useConvertAssetsMutation,
  useConvertAssetsPreview
} from '@/api/resources/arcade/convert'
import { Asset } from '@/api/resources/asset/get/schema'
import BulkAssetsActionView from '@/components/BulkAssetsActionView'
import Icon from '@/components/Icon'
import DefaultModal, {
  hideModal,
  showModal,
  switchModal
} from '@/components/Modals/ModalV2'
import { MODAL_ID } from '@/constants/modalId'
import useBreakpoints from '@/hooks/useBreakpoints'
import NiceModal from '@ebay/nice-modal-react'
import { FormProvider, useForm } from 'react-hook-form'
import { CONVERT_SUCCESS_MODAL_CONTENT } from './SuccessModalContent'
import { useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { useLayoutData } from '@/contexts/layout'
import { useAuth } from '@/contexts/auth'
import { ConvertForm } from './Form'
import { ConvertPreviewStats } from './Stats'
import ListItem from './ListItem'
import ListModal from './ListModal'
import { useSearchParams } from 'next/navigation'
import { SEARCH_PARAMS } from '@/constants/searchParams'
import { deleteSearchParams } from '@/util/queryHelpers'
import { notifyGenericError } from '@/components/QueryErrorNotifier/notifiers/Generic'
import { ApiError } from '@/api/core/errors'
import WarningAndErrors from './WarningInlineToast'
import { handle2FAAuthCodeRequest } from '@/components/Modals/ModalV2/TwoFAModal'
import { Toast } from '@/components'
import { useQueryClient } from '@tanstack/react-query'
import { path as userAssetsPath } from '@/api/resources/user/assets/get'
import { useArcadeRouting } from '@/features/arcade/hooks/routing'

interface Props {
  points: number
}

export default NiceModal.create(({ points }: Props) => {
  const { user, arcadeUser, fetchUser } = useAuth()
  const { leaderboard } = useArcadeRouting()
  const router = useRouter()
  const queryClient = useQueryClient()
  const searchParams = useSearchParams()
  const formMethods = useForm()
  const { isMedium } = useBreakpoints(['md'])
  const { selectedAssets, updateSelectedAssets } = useLayoutData()
  const hasLoaded = useRef(false)

  const selectedAssetIds = useMemo(
    () => selectedAssets.convert.map((asset: Asset) => asset.id),
    []
  )

  const handleError = async (error: ApiError) => {
    if (error.isArcadeClosed) {
      switchModal(MODAL_ID.arcade.convert, MODAL_ID.arcade.closed)
    } else if (error.is2FAAuthCodeRequest) {
      const code = await handle2FAAuthCodeRequest(error, user)
      if (code) {
        mutate({ asset_ids: selectedAssetIds, code: code as string })
      }
    } else {
      notifyGenericError({ error })
    }
  }

  const checkItemDetailDisplay = async (itemIds: Array<number>) => {
    const itemParamValue = searchParams.get(SEARCH_PARAMS.ITEM)

    if (!itemParamValue) return

    const shouldCloseDetail = itemIds.some(id => id === Number(itemParamValue))

    if (shouldCloseDetail) {
      await router.replace(
        deleteSearchParams([SEARCH_PARAMS.ITEM]),
        undefined,
        { shallow: true }
      )
    }
  }

  const { isLoading, data } = useConvertAssetsPreview(selectedAssetIds, {
    enabled: !hasLoaded.current,
    onError: handleError,
    onSuccess: ({ data }) => {
      const { assets, errors } = data

      // Abort flow if all assets are not owned
      if (assets.length === 0) {
        const itemsNotOwned =
          errors.length ===
          errors.filter(error => error.type === 'NOT_OWNED').length
        if (itemsNotOwned) {
          queryClient.invalidateQueries([userAssetsPath]).then()
          checkItemDetailDisplay(selectedAssetIds)
          hideModal(MODAL_ID.arcade.convert)
          updateSelectedAssets([])
          showNotOwnedToast()
        }
      }
    }
  })

  const { mutate, isLoading: isLoadingMutation } = useConvertAssetsMutation({
    onError: handleError,
    onSuccess: async response => {
      const { season_total, converted_asset_ids } = response.data
      const { rank } = season_total

      switchModal(MODAL_ID.arcade.convert, MODAL_ID.success, {
        ...CONVERT_SUCCESS_MODAL_CONTENT(
          { current: rank, previous: arcadeUser.player_rank },
          () => leaderboard.goTo().then(),
          () => hideModal(MODAL_ID.success)
        )
      })

      const notOwnedError = errors.find(error => error.type === 'NOT_OWNED')

      if (notOwnedError) {
        showNotOwnedToast()
        updateSelectedAssets(notOwnedError.assets.map(asset => asset.id))
      }
      queryClient.invalidateQueries([userAssetsPath]).then()
      checkItemDetailDisplay(converted_asset_ids)
      updateSelectedAssets([])
      fetchUser().then()
    }
  })

  // Guards against re-fetch on unmount
  useEffect(() => {
    if (!data) return
    hasLoaded.current = true
  }, [data])

  const previewData = data?.data
  const convertableAssets = previewData?.assets

  if (!convertableAssets?.length) return null

  const listings = previewData?.assets.filter(asset => asset.listing_id)
  const errors = previewData?.errors
  const convertableAssetsIds = convertableAssets?.map(asset => asset.id)

  const title = isMedium ? '' : 'Convert to Arcade Points'

  return (
    <FormProvider {...formMethods}>
      <DefaultModal
        className="w-full max-w-[980px]"
        shouldHideHeader={isMedium}
        title={title}
        id={MODAL_ID.arcade.convert}
      >
        {modalProps => (
          <BulkAssetsActionView
            title="Convert to Arcade Points"
            actionLabel="convert"
            description="Compete to stay at or above Rank 333 by converting Digital Pop! into Arcade Points. Converting will permanently remove the item from your inventory, and does not secure your position at this rank."
            subFooter={
              <p className="text-gray-300 body-xs md:mt-3">
                <sup>*</sup>The potential rank shown above may change after
                converting points, and your position may fall based on other
                players' participation.
              </p>
            }
            onClose={() => hideModal(MODAL_ID.arcade.convert)}
            onSubmit={() => mutate({ asset_ids: convertableAssetsIds })}
            primaryAction={{
              label: (
                <div className="flex gap-1 items-center">
                  <Icon name="arcade" className="h-[20px] w-[20px]" />
                  Convert To Points
                </div>
              ),
              theme: 'green-arcade',
              loading: isLoadingMutation
            }}
            listConfig={{
              preListSlot: (
                <WarningAndErrors listings={listings} errors={errors} />
              ),
              titleSuffix: 'to convert',
              items: convertableAssets,
              isLoading,
              ItemComponent: item => <ListItem item={item} />,
              onListModalTriggerClick: () => showModal('list-modal')
            }}
            modalProps={modalProps}
            formMethods={formMethods}
            footerSlot={
              <div className="md:hidden">
                <WarningAndErrors listings={listings} errors={errors} />
                <ConvertForm />
              </div>
            }
          >
            <>
              <ConvertPreviewStats
                points={points}
                potential_points={previewData?.potential_points}
                potential_rank={previewData?.potential_rank}
              />
              <ConvertForm className="max-md:hidden" />
            </>
          </BulkAssetsActionView>
        )}
      </DefaultModal>
      {!isMedium && <ListModal items={convertableAssets} />}
    </FormProvider>
  )
})

const showNotOwnedToast = () => {
  Toast({
    type: 'attention',
    description:
      'You no longer own one or more items selected. See your Activity for details.'
  })
}
